// The feeling pages: /sky-of-thoughts/<slug> (the Sky, gathered on one feeling)
// and the overview at /sky-of-thoughts/feelings. Shared with the server, which
// puts the same words in the page HTML for search engines.
// Founder voice ("I"), plain words, no advice lists. Keys match skyFeelings.ts.

import type { SkyFeelingKey } from './skyFeelings';

export type FeelingPage = {
  key: SkyFeelingKey;
  slug: string;        // /sky-of-thoughts/<slug>
  title: string;       // browser tab and search result
  description: string; // search result and link preview
  heading: string;     // first part of the heading
  headingEm: string;   // second part, in italics
  lead: string;        // one line under the heading
  note: string[];      // "Read more"
  line: string;        // the short line on the overview card
  emojis: string[];    // for the share image
  keywords: string;
};

export const FEELINGS_HUB = {
  path: '/sky-of-thoughts/feelings',
  title: 'Feelings in the Sky of Thoughts | Anonymous Thoughts | Zuzu Club',
  description: 'Lonely, heartbroken, stressed, anxious or hopeful? Read anonymous thoughts from people who feel the same, and add your own. Free, no signup.',
  heading: 'Whatever you feel,',
  headingEm: 'someone here has felt it too.',
  lead: 'Pick a feeling to see the thoughts people shared about it, anonymously. Then add yours, if you want to.',
  keywords: 'anonymous thoughts by feeling, feeling lonely, heartbreak, work stress, anxiety at night, grief, self-worth, sky of thoughts, share feelings anonymously',
};

export const FEELING_PAGES: FeelingPage[] = [
  {
    key: 'lonely', slug: 'lonely',
    title: 'Feeling Lonely, Even With People Around? | Sky of Thoughts | Zuzu Club',
    description: 'Feeling lonely, even in a full room? Read anonymous thoughts from people who feel the same, then add your own. Free, no signup, no judgment.',
    heading: 'Lonely, even with', headingEm: 'people around you?',
    lead: 'You are not the only one. These are thoughts from people who felt it too.',
    note: [
      'Loneliness does not only happen when you are alone. It can sit with you in a full room, in a group chat, at the end of a busy day.',
      'Here, people put it into words, anonymously. Reading them will not fix everything, but it can make the night feel a little less empty. When you are ready, add yours. Someone will read it and feel less alone too.',
    ],
    line: 'For when the room is full and you still feel unseen.',
    emojis: ['😢', '🌙', '😔', '💭', '🫂', '🤍', '🥺', '✨'],
    keywords: 'feeling lonely, lonely even with friends, feeling alone, loneliness at night, nobody understands me, anonymous lonely thoughts',
  },
  {
    key: 'heartbreak', slug: 'heartbreak',
    title: 'Heartbreak: Anonymous Thoughts From People Who Get It | Zuzu Club',
    description: 'Going through a breakup or missing someone? Read anonymous thoughts from people with a broken heart, and share yours. Free, no signup.',
    heading: 'Missing someone', headingEm: 'who is still out there?',
    lead: 'A broken heart is easier to carry when you see others carrying one too.',
    note: [
      'Heartbreak is strange. The person is still in the world, maybe living a normal day, and you are here replaying every message. It does not matter if it was a long love or something that never had a name.',
      'These are thoughts from people who know that feeling. Read a few, tap Me too if one sounds like you, and write your own when you are ready. You do not have to explain it perfectly.',
    ],
    line: 'Missing someone who is still out there, living their life.',
    emojis: ['💔', '🥀', '😭', '❤️‍🩹', '🌧️', '💌', '🫶', '🌙'],
    keywords: 'heartbreak, breakup, missing someone, how to get over a breakup, broken heart, anonymous breakup stories',
  },
  {
    key: 'work', slug: 'stress',
    title: 'Stressed About Work or Studies? Anonymous Thoughts | Zuzu Club',
    description: 'Burnt out by work, exams, a job hunt or deadlines? Read anonymous thoughts from people under the same pressure, and say yours out loud. Free, no signup.',
    heading: 'When the to-do list', headingEm: 'is longer than the day.',
    lead: 'Work, studies, job hunts, placements, deadlines. Here is what people carry home with them.',
    note: [
      'Some days the pressure does not stop when the laptop closes. It follows you to dinner, to bed, into your thoughts at 2 AM. It is easy to feel like you are the only one who is not coping.',
      'You are not. These are thoughts from people with the same weight on their shoulders. Read them, let one out yourself, or release it privately in Let Go. Sometimes saying it is the first breath of the day.',
    ],
    line: 'When the to-do list is longer than the day.',
    emojis: ['😮‍💨', '💼', '😩', '📚', '☕', '🤯', '🌙', '🍃'],
    keywords: 'work stress, burnout, exam stress, study pressure, stressed at work, tired of my job, job search stress, placement stress, interview anxiety, business stress, anonymous work stress',
  },
  {
    key: 'family', slug: 'family',
    title: 'Family Feelings You Can’t Say Out Loud | Sky of Thoughts | Zuzu Club',
    description: 'Family pressure, distance or fights? Read anonymous thoughts from people who feel the same about home, and share yours. Free, no signup.',
    heading: 'Love, and the weight', headingEm: 'that comes with it.',
    lead: 'The people closest to us can also be the hardest to talk to.',
    note: [
      'Family can be love and pressure at the same time. Expectations, silences, old fights, the things nobody says at the dinner table. You can be grateful and still hurt.',
      'Here people share those feelings without anyone knowing who they are. Read them, find the one that sounds like home, and add yours if it helps to set it down somewhere.',
    ],
    line: 'Love, and the weight that sometimes comes with it.',
    emojis: ['🏠', '😔', '🤍', '🫂', '😤', '🥲', '🌿', '🕊️'],
    keywords: 'family problems, family pressure, parents expectations, fights at home, feeling misunderstood by family, anonymous family stories',
  },
  {
    key: 'selfworth', slug: 'self-worth',
    title: 'Wondering If You Are Enough? Anonymous Thoughts | Zuzu Club',
    description: 'Feeling not good enough, or lost? Read anonymous thoughts from people asking the same questions about themselves, and share yours. Free, no signup.',
    heading: 'Wondering if', headingEm: 'you are enough?',
    lead: 'You are. And many people here have wondered the same thing.',
    note: [
      'It is a quiet feeling. Comparing your life to everyone else’s, chasing a goal and feeling nothing when you reach it, wondering who you are when nobody is watching.',
      'People here wrote those thoughts down, anonymously. Reading them can be a small reminder that this feeling is common, and that it is not the truth about you. Add yours, and let someone else feel less alone in it.',
    ],
    line: 'Wondering if you are enough. You are.',
    emojis: ['🌱', '🥺', '💭', '🌻', '✨', '😞', '🤍', '🌈'],
    keywords: 'not good enough, low self-worth, feeling worthless, comparing myself to others, feeling lost in life, anonymous self-worth thoughts',
  },
  {
    key: 'anxiety', slug: 'anxiety',
    title: 'Anxious Thoughts at Night? You Are Not Alone | Zuzu Club',
    description: 'Overthinking, worry or anxiety that won’t switch off? Read anonymous thoughts from people who feel it too, and let yours out. Free, no signup.',
    heading: 'When your mind', headingEm: 'won’t stop.',
    lead: 'Overthinking, worry, the 2 AM spiral. Others are awake with it too.',
    note: [
      'Anxiety is loud in a quiet room. The same thought on repeat, the what-ifs, the heart that will not slow down. It can feel like everyone else is asleep and fine.',
      'Many people are awake with the same feeling. Read what they shared. Writing your own worry down can make it a little smaller. If it is too heavy tonight, Let Go is there for one minute of calm.',
    ],
    line: 'When your mind won’t stop, especially at night.',
    emojis: ['😟', '🌊', '😰', '🌙', '💭', '🫨', '🍃', '🕯️'],
    keywords: 'anxiety at night, overthinking, can’t stop worrying, anxious thoughts, can’t sleep, anonymous anxiety stories',
  },
  {
    key: 'grief', slug: 'grief',
    title: 'Grieving Someone? Anonymous Thoughts on Loss | Zuzu Club',
    description: 'Missing someone you lost? Read gentle, anonymous thoughts from people who are grieving too, and share a memory or a feeling. Free, no signup.',
    heading: 'Carrying someone', headingEm: 'who is no longer here.',
    lead: 'Grief has no timeline. Here, people share theirs gently.',
    note: [
      'Grief comes in waves. Some days are fine, and then a song or a smell brings it all back. It can be about a person, a pet, a friendship, or a life you thought you would have.',
      'These thoughts are from people missing someone too. Read them slowly. If you want, write a memory, a feeling, or just their name. There is no right way to grieve.',
    ],
    line: 'Carrying someone who is no longer here.',
    emojis: ['🕯️', '🥀', '🤍', '😢', '🌌', '🕊️', '💫', '🌙'],
    keywords: 'grief, grieving, missing someone who died, loss, coping with loss, anonymous grief stories',
  },
  {
    key: 'anger', slug: 'anger',
    title: 'Angry and Need to Say It? Anonymous Thoughts | Zuzu Club',
    description: 'Something unfair, someone who hurt you, anger with nowhere to go? Read anonymous thoughts from people who feel the same, and say yours. Free, no signup.',
    heading: 'When it’s unfair', headingEm: 'and you need to say it.',
    lead: 'Anger is a feeling too. Here it can be said without hurting anyone.',
    note: [
      'Sometimes you are just angry. At a person, at a situation, at yourself, at how things turned out. Holding it in can make it heavier.',
      'Here people let it out, anonymously and without naming anyone. Read what they shared. Then write yours, or release it in Let Go and watch it fade. Saying it is not the same as acting on it.',
    ],
    line: 'When it’s unfair and you need to say it.',
    emojis: ['😡', '😤', '🔥', '💢', '😠', '🌋', '🌧️', '🍃'],
    keywords: 'angry, frustrated, feeling angry, anger at someone, it’s unfair, vent anger anonymously',
  },
  {
    key: 'hope', slug: 'hope',
    title: 'Small Moments of Hope | Sky of Thoughts | Zuzu Club',
    description: 'Feeling a little better, grateful or hopeful? Read anonymous thoughts from people finding small lights, and share yours. Free, no signup.',
    heading: 'Small lights,', headingEm: 'on the way back to yourself.',
    lead: 'Not every thought here is heavy. These ones feel a little lighter.',
    note: [
      'Hope does not always look big. A good conversation, a calm morning, a day that felt a bit easier than the last one. It still counts.',
      'These are thoughts from people noticing those small lights. Read them on a hard day. And if you have one of your own, share it. It might be exactly what someone needs to read tonight.',
    ],
    line: 'Small lights, on the way back to yourself.',
    emojis: ['😊', '🌅', '🌻', '✨', '🌈', '🫶', '☀️', '🦋'],
    keywords: 'hope, feeling hopeful, gratitude, feeling better, small wins, anonymous hopeful stories',
  },
  {
    key: 'other', slug: 'other-emotions',
    title: 'Feelings That Don’t Fit in a Box | Sky of Thoughts | Zuzu Club',
    description: 'Not sure what you feel? Read anonymous thoughts that don’t fit one word, and share your own. Free, no signup, no judgment.',
    heading: 'Feelings that don’t', headingEm: 'fit in a box.',
    lead: 'Not everything has a name. These thoughts don’t need one.',
    note: [
      'Some feelings are hard to name. A mix of things, a strange mood, something you only understand once you write it down.',
      'These thoughts are like that. Read them, and share yours even if you are not sure what it is yet. The Sky does not need a label.',
    ],
    line: 'Feelings that don’t fit in a box.',
    emojis: ['💭', '🌀', '🤔', '🌙', '✨', '🫧', '🌫️', '💫'],
    keywords: 'mixed feelings, confusing emotions, don’t know what I feel, share feelings anonymously, anonymous thoughts',
  },
];

export const feelingPageBySlug = (slug?: string) => FEELING_PAGES.find(p => p.slug === slug);
export const feelingPageByKey = (key?: string) => FEELING_PAGES.find(p => p.key === key);
export const feelingPath = (key: string) => {
  const p = feelingPageByKey(key);
  return p ? `/sky-of-thoughts/${p.slug}` : '/sky-of-thoughts';
};
